// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Phase 4: Template Preview & Reporting API
// Endpoints for template resolution preview and usage analytics

export interface TemplateResolutionInfo {
  brokerageCustomTemplateId?: string;
  brokerageCustomTemplateName?: string;
  policyTypeDefaultTemplateId?: string;
  policyTypeDefaultTemplateName?: string;
  globalDefaultTemplateId?: string;
  globalDefaultTemplateName?: string;
  resolvedSource: string;
  resolvedTemplateId: string;
  resolvedTemplateName: string;
}

export interface TemplateUsageStat {
  templateId: string;
  templateName: string;
  usageCount: number;
  uniqueLOACount: number;
  usagePercentage: number;
}

export interface ResolutionSourceDistribution {
  brokerageCustomCount: number;
  policyTypeDefaultCount: number;
  globalDefaultCount: number;
  brokerageCustomPercentage: number;
  policyTypeDefaultPercentage: number;
  globalDefaultPercentage: number;
}

export interface TemplateUsageReport {
  totalLOAs: number;
  loasWithTemplateTracking: number;
  templateUsageStats: TemplateUsageStat[];
  resolutionSourceDistribution: ResolutionSourceDistribution;
}

const API_BASE = '/api/admin/loa';

export const loaAdminApi = {
  /**
   * Preview which template will be used for a given brokerage and policy type combination
   */
  async previewTemplateResolution(
    brokerageId: string,
    policyTypeId: string
  ): Promise<TemplateResolutionInfo> {
    const queryParams = new URLSearchParams({
      brokerageId,
      policyTypeId
    });

    const response = await fetch(
      `${API_BASE}/preview-template?${queryParams}`,
      {
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to preview template resolution');
    }

    return response.json();
  },

  /**
   * Get template usage analytics report
   */
  async getTemplateUsageReport(): Promise<TemplateUsageReport> {
    const response = await fetch(`${API_BASE}/template-usage-report`, {
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to fetch template usage report');
    }

    return response.json();
  }
};
